import { hasAuthParams } from 'react-oidc-context'
import { createBrowserRouter, Navigate, RouterProvider, useLocation } from 'react-router'
import { FullStory } from '@fullstory/browser'
import { LicenseInfo } from '@mui/x-license'

import LoggingIn from './components/auth/logging-in'
import LoggingOut from './components/auth/logging-out'
import Redirecting from './components/auth/redirecting'
import RouteWrapper from './components/route-wrapper'
import AppProvider from './providers/app-provider'
import { useAuthentication } from './providers/auth-provider'
import Accounts from './routes/accounts'
import Bookmarks from './routes/bookmarks'
import Dashboard from './routes/dashboard'
import Devices from './routes/devices'
import Invite from './routes/invite'
import Login from './routes/login'
import NewlyAddedAccounts from './routes/newly-added-accounts'
import Orders from './routes/orders'
import People from './routes/people'
import ReleaseNotes from './routes/release-notes'
import ReleaseNoteForm from './routes/release-notes/release-note-form'
import SearchResults from './routes/search-results'
import { checkIsRecentlyLoggedOut } from './utils/helpers'

const PrivateRouteWrapper = () => {
  const location = useLocation()
  const { auth, isAuthenticated } = useAuthentication()

  if (auth.activeNavigator === 'signoutRedirect' || auth.activeNavigator === 'signoutSilent') return <LoggingOut />
  if (hasAuthParams()) return <LoggingIn />
  if (auth.isLoading || auth.activeNavigator === 'signinRedirect' || auth.activeNavigator === 'signinSilent') return <Redirecting />
  if (!isAuthenticated) {
    const isRecentlyLoggedOut = checkIsRecentlyLoggedOut()
    return <Navigate to="/login" replace state={isRecentlyLoggedOut ? {} : { from: location }} />
  }
  return (
    <AppProvider>
      <RouteWrapper />
    </AppProvider>
  )
}

export const routes = [
  {
    path: '/',
    element: <Navigate to="/login" replace />,
  },
  {
    path: '/login',
    element: <Login />,
  },
  {
    path: '/invite',
    element: <Invite />,
  },
  {
    path: '/auth/callback/meshify',
    element: <Login />,
  },
  {
    path: '*',
    element: <PrivateRouteWrapper />,
  },
  {
    element: <PrivateRouteWrapper />,
    children: [
      {
        path: '/auth/callback/meshify',
        element: <LoggingIn />,
      },
      {
        path: '/dashboard',
        element: <Dashboard />,
      },
      {
        path: '/newly-added-accounts',
        element: <NewlyAddedAccounts />,
      },
      {
        path: '/bookmarks',
        element: <Bookmarks />,
      },
      {
        path: '/accounts',
        element: <Accounts />,
      },
      {
        path: '/accounts/:accountId', // Handle legacy bookmarks; this page will redirect to the route below, using the default tenant id
        element: <Accounts />,
      },
      {
        path: '/tenant/:tenantId/accounts/:accountId',
        element: <Accounts />,
      },
      {
        path: '/tenant/:tenantId/people/:userId',
        element: <People />,
      },
      {
        path: '/tenant/:tenantId/devices/:deviceId',
        element: <Devices />,
      },
      {
        path: '/tenant/:tenantId/orders/:orderId',
        element: <Orders />,
      },
      {
        path: '/profile',
        element: <People />,
      },
      {
        path: '/release-notes',
        element: <ReleaseNotes />,
      },
      {
        path: '/release-notes/:releaseNoteId',
        element: <ReleaseNoteForm />,
      },
      {
        path: '/search-results',
        element: <SearchResults />,
      },
    ],
  },
]

const multiTenantRouter = createBrowserRouter(routes)

const MUI_LICENSE_KEY = import.meta.env.VITE_MUI_LICENSE_KEY
LicenseInfo.setLicenseKey(MUI_LICENSE_KEY)

const App = () => {
  const { ownUser } = useAuthentication()

  if (ownUser)
    FullStory('setIdentity', {
      uid: String(ownUser.id),
      properties: {
        userId: ownUser.id,
      },
    })

  return <RouterProvider router={multiTenantRouter} />
}

export default App
